import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';

import { selectAppState } from '../../state/selectors';
import { AppState } from '../../state/store';
import { ApiLogService, ApiLogEntry } from '../../../shared/services/api-log.service';
import { AppUtilityService } from '../../../shared/services/app-utility.service';
import { CommonDialogData } from '../../../shared/models/dialog.model';
import { formatTableCellValue } from '../../../shared/utils/helper';

interface AuditTrailRow {
  name: string;
  userId?: string | null;
  isSuperUser?: boolean | null;
  date: string | null;
  action?: string | null;
  appVersion?: string | null;
  comment?: string | null;
}

@Component({
  selector: 'app-audit-trail',
  templateUrl: './audit-trail.component.html',
  styleUrl: './audit-trail.component.scss'
})
export class AuditTrailComponent implements OnInit, OnDestroy {
  appState$: Observable<AppState> = this.store.select(selectAppState);
  isPrintView = false;
  auditTrailColumns: string[] = ['name', 'userId', 'date', 'isSuperUser', 'action', 'appVersion', 'comment'];
  auditTrailData: AuditTrailRow[] = [];
  auditTrailDataSource = new MatTableDataSource<AuditTrailRow>([]);
  auditTrailPrintPages: AuditTrailRow[][] = [];
  studyName = '';
  panelName = '';
  specVersion = '';
  appVersion = '';
  /** The audit trail is only written server-side on save, so an edited study cannot be printed. */
  canPrintAuditTrail = false;

  /** Columns that show the Calculations-table-style checkbox dropdown filter. */
  readonly dropdownFilterKeys: string[] = ['name', 'action'];
  allColumnValues = new Map<string, string[]>();
  columnExcludedValues = new Map<string, Set<string>>();
  private columnFilterVersion = 0;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private apiLogService: ApiLogService,
    private router: Router,
    private appUtilityService: AppUtilityService
  ) {}

  ngOnInit(): void {
    this.isPrintView = this.router?.url?.includes('print');
    this.auditTrailDataSource.filterPredicate = this.buildFilterPredicate();
    this.appState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(appState => {
        this.auditTrailData = appState.selectedStudy?.auditTrail ?? [];
        this.auditTrailDataSource.data = this.auditTrailData;
        this.rebuildColumnValues();
        this.applyFilters();
        this.auditTrailPrintPages = this.buildPrintPages(this.auditTrailData, 15);
        this.studyName = appState.selectedStudy?.studyName ?? '';
        this.panelName = appState.selectedStudy?.panelSpecs?.kitname ?? '';
        this.specVersion = appState.selectedStudy?.panelSpecs?.revisionNumber ?? '';
        this.appVersion = appState.appVersion ?? '';
        this.canPrintAuditTrail = !!appState.existingStudy && !appState.areStudyChangesPending;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Identifies the document in the print preview bar, beside the study name. */
  get printContextLine(): string {
    return [
      this.panelName,
      this.specVersion ? `Spec Rev ${this.specVersion}` : '',
      this.appVersion ? `App ${this.appVersion}` : ''
    ].filter(Boolean).join(' · ');
  }

  /**
   * Returns the distinct, formatted values available for a filterable column,
   * used to render the checkbox list in that column's filter menu.
   */
  getUniqueColumnValues(key: string): string[] {
    return this.allColumnValues.get(key) ?? [];
  }

  /** Whether the given formatted value is currently checked (i.e. not excluded) for a column. */
  isColumnValueChecked(key: string, value: string): boolean {
    const excluded = this.columnExcludedValues.get(key);
    return !excluded || !excluded.has(value);
  }

  /** Toggles whether a single value is excluded from the given column's filter. */
  toggleColumnValue(key: string, value: string): void {
    if (!this.columnExcludedValues.has(key)) {
      this.columnExcludedValues.set(key, new Set());
    }
    const excluded = this.columnExcludedValues.get(key)!;
    if (excluded.has(value)) {
      excluded.delete(value);
    } else {
      excluded.add(value);
    }
    this.columnFilterVersion += 1;
    this.applyFilters();
  }

  /** True when some, but not all, of a column's values are excluded (drives the active filter-icon style). */
  isColumnFilterActive(key: string): boolean {
    const excluded = this.columnExcludedValues.get(key);
    return !!excluded && excluded.size > 0 && excluded.size < this.getUniqueColumnValues(key).length;
  }

  /** Whether every value in the given column is currently selected (none excluded). */
  areAllSelected(key: string): boolean {
    const excluded = this.columnExcludedValues.get(key);
    return !excluded || excluded.size === 0;
  }

  /** Toggles the column's "(Select All)" checkbox: clears exclusions, or excludes everything. */
  toggleSelectAll(key: string): void {
    if (this.areAllSelected(key)) {
      this.columnExcludedValues.set(key, new Set(this.getUniqueColumnValues(key)));
    } else {
      this.columnExcludedValues.set(key, new Set());
    }
    this.columnFilterVersion += 1;
    this.applyFilters();
  }

  /** Rebuilds the distinct value list for each filterable column from the current rows. */
  private rebuildColumnValues(): void {
    this.columnExcludedValues.clear();
    this.dropdownFilterKeys.forEach(key => {
      const seen = new Set<string>();
      this.auditTrailData.forEach(row => {
        seen.add(formatTableCellValue((row as unknown as Record<string, unknown>)[key]));
      });
      const sorted = Array.from(seen).sort((a, b) => a.localeCompare(b));
      this.allColumnValues.set(key, sorted);
      this.columnExcludedValues.set(key, new Set());
    });
  }

  /** Re-triggers MatTableDataSource filtering by assigning a new filter value. */
  private applyFilters(): void {
    this.auditTrailDataSource.filter = JSON.stringify({ v: this.columnFilterVersion });
  }

  private buildFilterPredicate(): (data: AuditTrailRow, filter: string) => boolean {
    return (data: AuditTrailRow): boolean => {
      const record = data as unknown as Record<string, unknown>;
      for (const [key, excluded] of this.columnExcludedValues) {
        if (!excluded.size) {
          continue;
        }
        if (excluded.has(formatTableCellValue(record[key]))) {
          return false;
        }
      }
      return true;
    };
  }

  downloadTroubleshootingLog(): void {
    const entries = this.apiLogService.getEntries();
    const content = this.formatLogEntries(entries);
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    link.href = url;
    link.download = `bpx-troubleshooting-log-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  navigateToPrintView(): void {
    if (!this.canPrintAuditTrail) {
      this.appUtilityService.displayAlertDialog({
        title: 'UNSAVED CHANGES',
        message: 'This study has unsaved changes. The audit trail records only what has been saved '
          + 'to the server, so printing now would produce a record that does not match the saved '
          + 'study. Save the study, then print the audit trail.',
        okText: 'OK',
        dismissText: '',
        okAction: () => { },
        dismissAction: () => { }
      } as CommonDialogData);
      return;
    }
    this.router.navigateByUrl('/(print:print/preview-audit-trail)');
  }

  private formatLogEntries(entries: ApiLogEntry[]): string {
    const header = 'ID,Timestamp,Method,URL,Payload,Response,Code';
    if (!entries.length) {
      return `${header}\n`;
    }
    const rows = entries.map(entry => this.formatCsvRow(entry));
    return [header, ...rows].join('\n') + '\n';
  }

  private formatCsvRow(entry: ApiLogEntry): string {
    const payload = this.safeJsonString(entry.payload);
    const response = this.safeJsonString(entry.response);
    const values = [
      entry.id,
      entry.timestamp,
      entry.method,
      entry.url,
      payload,
      response,
      entry.code ?? ''
    ];
    return values.map(value => this.csvEscape(value)).join(',');
  }

  private safeJsonString(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    try {
      return JSON.stringify(value);
    } catch (error) {
      return `Failed to stringify value: ${String(error)}`;
    }
  }

  private csvEscape(value: unknown): string {
    const text = String(value ?? '');
    const escaped = text.replace(/"/g, '""');
    return `"${escaped}"`;
  }

  private buildPrintPages(rows: AuditTrailRow[], pageSize: number): AuditTrailRow[][] {
    if (!rows.length) {
      return [[]];
    }

    const pages: AuditTrailRow[][] = [];
    for (let i = 0; i < rows.length; i += pageSize) {
      pages.push(rows.slice(i, i + pageSize));
    }
    return pages;
  }
}
